import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useEffect, useState } from 'react';
import { QRCodeSVG } from 'qrcode.react';

import type { DepositResponse } from '@shared/contract';

import { DemoApiError } from '../api/client';
import { useSelection } from '../selection/SelectionContext';
import { useSession } from '../session/SessionContext';
import { brand } from '../theme';
import { MonoText } from '../ui/Brand';

function depositErrorCopy(err: unknown): string {
  if (err instanceof DemoApiError) {
    if (err.code === 'CHEDDAR_FORBIDDEN') {
      return 'Cheddar rejected this request (403). The demo org likely has an IP allowlist enabled — turn it off for the Lambda egress.';
    }
    if (err.code === 'CHEDDAR_RISK_BLOCKED') {
      return 'Cheddar blocked this request (451) for risk reasons. Check the demo org risk thresholds.';
    }
    return err.message;
  }
  return err instanceof Error ? err.message : 'Could not load deposit address';
}

export interface DepositPanelProps {
  /** When true, omit the outer card shell (parent already provides one). */
  embedded?: boolean;
}

export default function DepositPanel({ embedded = false }: DepositPanelProps) {
  const { session, api } = useSession();
  const { selection, provisioning } = useSelection();
  const [deposit, setDeposit] = useState<DepositResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [retryToken, setRetryToken] = useState(0);

  const mintId = selection?.mintId;
  const chain = selection?.chain;
  const customerId = session?.customerId;

  useEffect(() => {
    if (!customerId || mintId == null) {
      setDeposit(null);
      setError(null);
      return;
    }

    let cancelled = false;
    setDeposit(null);
    setError(null);
    setLoading(true);
    setCopied(false);

    void (async () => {
      try {
        const result = await api.deposit({
          customerId,
          mintId,
        });
        if (!cancelled) {
          setDeposit(result);
        }
      } catch (err) {
        if (!cancelled) {
          setError(depositErrorCopy(err));
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, customerId, mintId, chain, retryToken]);

  if (!selection) {
    return null;
  }

  const busy = loading || provisioning;

  const body = (
    <>
      {busy ? (
        <Stack alignItems="center" spacing={1.25} sx={{ py: 5 }}>
          <CircularProgress size={32} />
          <Typography sx={{ fontSize: 13, color: brand.faint }}>
            Loading deposit address…
          </Typography>
        </Stack>
      ) : error ? (
        <Stack spacing={1.5} sx={{ mt: embedded ? 0 : 2 }}>
          <Alert severity="error">{error}</Alert>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setRetryToken((n) => n + 1)}
            sx={{ alignSelf: 'flex-start' }}
          >
            Retry
          </Button>
        </Stack>
      ) : deposit ? (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={{ xs: 2, sm: 3.25 }}
          alignItems={{ xs: 'center', sm: 'flex-start' }}
          sx={{ mt: embedded ? 0.5 : 2 }}
        >
          <Box
            sx={{
              flex: 'none',
              p: 1.5,
              borderRadius: '18px',
              backgroundColor: brand.surface,
              border: `1px solid ${brand.hairline}`,
              boxShadow: '0 10px 24px -18px rgba(28,37,46,.6)',
              lineHeight: 0,
            }}
          >
            <QRCodeSVG
              value={deposit.address}
              size={168}
              level="M"
              bgColor="#FFFFFF"
              fgColor={brand.ink}
            />
          </Box>

          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 0, width: '100%' }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.25,
                p: 1.5,
                borderRadius: '13px',
                backgroundColor: brand.canvasSunk,
                border: `1px solid ${brand.hairline}`,
              }}
            >
              <MonoText size={12.5} sx={{ flex: 1, minWidth: 0 }}>
                {deposit.address}
              </MonoText>
              <Button
                variant="contained"
                color="secondary"
                size="small"
                sx={{ flex: 'none' }}
                onClick={async () => {
                  try {
                    await navigator.clipboard.writeText(deposit.address);
                    setCopied(true);
                    window.setTimeout(() => setCopied(false), 2000);
                  } catch {
                    setCopied(false);
                  }
                }}
              >
                {copied ? 'Copied ✓' : 'Copy'}
              </Button>
            </Box>

            <Typography
              sx={{ fontSize: 12.5, color: brand.faint, lineHeight: 1.55, textWrap: 'pretty' }}
            >
              Send only <strong style={{ color: brand.body }}>{selection.symbol}</strong> on{' '}
              <strong style={{ color: brand.body }}>{selection.chain}</strong> to this
              address. Wrong token or chain may be unrecoverable. Mainnet — real funds.
            </Typography>
          </Stack>
        </Stack>
      ) : null}
    </>
  );

  if (embedded) {
    return <Box data-testid="deposit-panel">{body}</Box>;
  }

  return <Box data-testid="deposit-card">{body}</Box>;
}
